import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Filter, Camera, ClipboardList, Wrench, Clock, Trash2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import PageWrapper from '@/components/layout/PageWrapper';
import DataTable, { type ColumnDef } from '@/components/shared/DataTable';
import StatusBadge from '@/components/shared/StatusBadge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { equipmentApi, equipmentTypesApi, locationsApi, companiesListApi, pmAssignmentsApi } from '@/api';
import PmAssignmentForm from '@/components/pmTracking/PmAssignmentForm';
import EquipmentReport from './EquipmentReport';
import { resizeImage } from '@/lib/resizeImage';
import axios from 'axios';
import { useAuth } from '@/auth/AuthContext';
import { formatDate } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';
import type { Equipment as EquipmentType } from '@/types';

const equipmentSchema = z.object({
  unitNumber: z.string().min(1, 'Unit number is required'),
  equipmentTypeId: z.number({ required_error: 'Equipment type is required' }).int().positive(),
  companyId: z.number({ required_error: 'Company is required' }).int().positive(),
  locationId: z.number().int().positive().nullable().optional(),
  description: z.string().min(1, 'Description is required'),
  vinSerial: z.string().optional(),
  dateInService: z.string().optional(),
  assetStatus: z.enum(['Active', 'Retired']),
});

type EquipmentFormData = z.infer<typeof equipmentSchema>;

// ── PM Assignments dialog ──────────────────────────────────────────────────────

function PmAssignmentsDialog({
  equipment,
  open,
  onOpenChange,
}: {
  equipment: EquipmentType | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [formMode, setFormMode] = useState<'closed' | 'add' | 'edit'>('closed');
  const [editingAssignment, setEditingAssignment] = useState<import('@/types').PmAssignment | null>(null);

  const { data: assignments = [], isLoading } = useQuery({
    queryKey: ['pmAssignments', equipment?.id],
    queryFn: () => pmAssignmentsApi.list({ equipmentId: equipment!.id }),
    enabled: !!equipment,
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: number) => pmAssignmentsApi.deactivate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pmAssignments', equipment?.id] });
      toast({ title: 'PM assignment removed' });
    },
    onError: () => toast({ title: 'Failed to remove', variant: 'destructive' }),
  });

  function closeForm() {
    setFormMode('closed');
    setEditingAssignment(null);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>PM Assignments — {equipment?.unitNumber}</DialogTitle>
        </DialogHeader>

        {formMode === 'closed' ? (
          <div className="space-y-3">
            {isLoading ? (
              <div className="h-16 animate-pulse rounded bg-muted" />
            ) : assignments.filter((a) => a.isActive).length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No PM types assigned.</p>
            ) : (
              assignments.filter((a) => a.isActive).map((a) => (
                <div key={a.id} className="flex items-center justify-between rounded-md border border-border px-3 py-2">
                  <div className="flex items-center gap-2">
                    {a.pmType?.color && (
                      <span className="inline-block h-3 w-3 rounded-full" style={{ backgroundColor: a.pmType.color }} />
                    )}
                    <div>
                      <p className="text-sm font-medium">{a.pmType?.name ?? `PM #${a.pmTypeId}`}</p>
                      <p className="text-xs text-muted-foreground">
                        {a.nextDueDate ? `Due ${formatDate(a.nextDueDate)}` : 'No due date computed'}
                        {a.lastCompletedDate ? ` · Last: ${formatDate(a.lastCompletedDate)}` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => { setEditingAssignment(a); setFormMode('edit'); }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                      onClick={() => deactivateMutation.mutate(a.id)}
                      disabled={deactivateMutation.isPending}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))
            )}

            <Button
              size="sm"
              variant="outline"
              className="w-full"
              onClick={() => setFormMode('add')}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add PM Type
            </Button>
          </div>
        ) : (
          <PmAssignmentForm
            existing={formMode === 'edit' ? editingAssignment ?? undefined : undefined}
            defaultEquipmentId={equipment?.id}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ['pmAssignments', equipment?.id] });
              closeForm();
            }}
            onCancel={closeForm}
          />
        )}

        {formMode === 'closed' && (
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

const TYPE_COLORS: Record<string, string> = {
  Truck: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  Trailer: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  Other: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
};

export default function Equipment() {
  const { hasRole } = useAuth();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<EquipmentType | null>(null);
  const [pmTarget, setPmTarget] = useState<EquipmentType | null>(null);
  const [reportTargetId, setReportTargetId] = useState<number | null>(null);

  // Photo state — used inside the edit/create dialog
  const [pendingPhotoFile, setPendingPhotoFile] = useState<File | null>(null);
  const [pendingPhotoPreview, setPendingPhotoPreview] = useState<string | null>(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canEdit = hasRole(['equipment_admin', 'super_admin']);
  const [unitNumberDuplicate, setUnitNumberDuplicate] = useState(false);

  const { data: equipmentTypes } = useQuery({
    queryKey: ['equipment-types'],
    queryFn: () => equipmentTypesApi.list(),
  });

  const { data: locationsList } = useQuery({
    queryKey: ['locations'],
    queryFn: () => locationsApi.list(),
  });

  const { data: companiesList } = useQuery({
    queryKey: ['companies-list'],
    queryFn: () => companiesListApi.list(),
  });

  const [companyFilter, setCompanyFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('Active');

  const { data, isLoading } = useQuery({
    queryKey: ['equipment', companyFilter, statusFilter],
    queryFn: () => equipmentApi.list({
      ...(companyFilter !== 'all' ? { company: companyFilter } : {}),
      ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
    }),
  });

  const createMutation = useMutation({
    mutationFn: (data: EquipmentFormData) => equipmentApi.create(data),
    onSuccess: async (created) => {
      if (pendingPhotoFile) {
        await uploadPhoto(created.id, pendingPhotoFile);
      }
      queryClient.invalidateQueries({ queryKey: ['equipment'] });
      toast({ title: 'Equipment added' });
      closeDialog();
    },
    onError: () => toast({ title: 'Failed to save', variant: 'destructive' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: EquipmentFormData }) =>
      equipmentApi.update(String(id), data),
    onSuccess: async (updated) => {
      if (pendingPhotoFile) {
        await uploadPhoto(updated.id, pendingPhotoFile);
      }
      queryClient.invalidateQueries({ queryKey: ['equipment'] });
      toast({ title: 'Equipment updated' });
      closeDialog();
    },
    onError: () => toast({ title: 'Failed to update', variant: 'destructive' }),
  });

  const form = useForm<EquipmentFormData>({
    resolver: zodResolver(equipmentSchema),
    defaultValues: {
      unitNumber: '',
      equipmentTypeId: undefined,
      companyId: undefined,
      locationId: undefined,
      description: '',
      vinSerial: '',
      dateInService: '',
      assetStatus: 'Active',
    },
  });

  function closeDialog() {
    setDialogOpen(false);
    setEditTarget(null);
    setPendingPhotoFile(null);
    if (pendingPhotoPreview) {
      URL.revokeObjectURL(pendingPhotoPreview);
      setPendingPhotoPreview(null);
    }
  }

  function openAdd() {
    setEditTarget(null);
    setPendingPhotoFile(null);
    setPendingPhotoPreview(null);
    setUnitNumberDuplicate(false);
    form.reset({
      unitNumber: '',
      equipmentTypeId: undefined,
      companyId: undefined,
      locationId: null,
      description: '',
      vinSerial: '',
      dateInService: '',
      assetStatus: 'Active',
    });
    setDialogOpen(true);
  }

  function openEdit(eq: EquipmentType) {
    setEditTarget(eq);
    setPendingPhotoFile(null);
    setPendingPhotoPreview(null);
    setUnitNumberDuplicate(false);
    form.reset({
      unitNumber: eq.unitNumber,
      equipmentTypeId: eq.equipmentTypeId ?? undefined,
      companyId: eq.companyId ?? undefined,
      locationId: eq.locationId ?? null,
      description: eq.description ?? '',
      vinSerial: eq.vinSerial ?? '',
      dateInService: eq.dateInService ?? '',
      assetStatus: eq.assetStatus,
    });
    setDialogOpen(true);
  }

  async function selectPhotoFile(file: File) {
    if (pendingPhotoPreview) URL.revokeObjectURL(pendingPhotoPreview);
    const resized = await resizeImage(file);
    setPendingPhotoFile(resized);
    setPendingPhotoPreview(URL.createObjectURL(resized));
  }

  async function uploadPhoto(equipmentId: number, file: File) {
    setPhotoUploading(true);
    try {
      const { uploadUrl, key } = await equipmentApi.getPhotoUploadUrl(equipmentId, 'image/jpeg');
      await axios.put(uploadUrl, file, { headers: { 'Content-Type': file.type } });
      await equipmentApi.savePhotoKey(equipmentId, key);
    } catch {
      toast({ title: 'Equipment saved but photo upload failed', variant: 'destructive' });
    } finally {
      setPhotoUploading(false);
    }
  }

  async function onSubmit(values: EquipmentFormData) {
    if (editTarget) {
      await updateMutation.mutateAsync({ id: editTarget.id, data: values });
    } else {
      await createMutation.mutateAsync(values);
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending || photoUploading;

  const columns: ColumnDef<EquipmentType>[] = [
    {
      key: 'unitNumber',
      header: 'Unit #',
      sortable: true,
      cell: (row) => <span className="font-mono font-medium">{row.unitNumber}</span>,
    },
    {
      key: 'equipmentType',
      header: 'Type',
      cell: (row) => (
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${TYPE_COLORS[row.equipmentType] ?? TYPE_COLORS['Other']}`}
        >
          {row.equipmentType}
        </span>
      ),
    },
    {
      key: 'company',
      header: 'Company',
      sortable: true,
      cell: (row) => row.company ?? '—',
    },
    {
      key: 'description',
      header: 'Description',
      cell: (row) => <span className="text-muted-foreground">{row.description ?? '—'}</span>,
    },
    {
      key: 'vinSerial',
      header: 'VIN',
      cell: (row) => (
        <span className="font-mono text-xs text-muted-foreground">
          {row.vinSerial ?? '—'}
        </span>
      ),
    },
    {
      key: 'location',
      header: 'Location',
      cell: (row) => (
        <span className="text-muted-foreground text-sm">{row.location ?? '—'}</span>
      ),
    },
    {
      key: 'totalRepairCost',
      header: 'Repair Costs',
      cell: (row) =>
        row.totalRepairCost != null && row.totalRepairCost > 0
          ? <span className="font-mono text-sm">${(row.totalRepairCost / 100).toFixed(2)}</span>
          : <span className="text-muted-foreground text-sm">—</span>,
    },
    {
      key: 'assetStatus',
      header: 'Status',
      cell: (row) => <StatusBadge status={row.assetStatus} />,
    },
    {
      key: 'photo',
      header: '',
      cell: (row) =>
        row.photoUrl ? (
          <button
            className="block w-10 h-10 rounded overflow-hidden border border-border hover:opacity-80 transition-opacity flex-shrink-0"
            title="View / replace photo"
            onClick={(e) => { e.stopPropagation(); openEdit(row); }}
          >
            <img src={row.photoUrl} alt="" className="w-full h-full object-cover" />
          </button>
        ) : canEdit ? (
          <Button
            size="sm"
            variant="ghost"
            className="text-muted-foreground"
            title="Add photo"
            onClick={(e) => { e.stopPropagation(); openEdit(row); }}
          >
            <Camera className="h-4 w-4" />
          </Button>
        ) : null,
    },
    {
      key: 'report',
      header: '',
      cell: (row: EquipmentType) => (
        <Button
          size="sm"
          variant="ghost"
          title="View History Report"
          onClick={(e) => { e.stopPropagation(); setReportTargetId(row.id); }}
        >
          <ClipboardList className="h-4 w-4" />
        </Button>
      ),
    } as ColumnDef<EquipmentType>,
    ...(canEdit
      ? [
          {
            key: 'actions',
            header: '',
            cell: (row: EquipmentType) => (
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  title="PM Assignments"
                  onClick={(e) => { e.stopPropagation(); setPmTarget(row); }}
                >
                  <span className="relative flex items-center">
                    <Wrench className="h-4 w-4" />
                    <Clock className="h-2.5 w-2.5 absolute -bottom-1 -right-1" />
                  </span>
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => { e.stopPropagation(); openEdit(row); }}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              </div>
            ),
          } as ColumnDef<EquipmentType>,
        ]
      : []),
  ];

  // The photo to display in the dialog: pending preview > existing saved URL
  const dialogPhotoSrc = pendingPhotoPreview ?? editTarget?.photoUrl ?? null;

  return (
    <PageWrapper
      title="Equipment"
      subtitle="Manage trucks, trailers, and other fleet equipment"
      actions={
        canEdit ? (
          <Button onClick={openAdd}>
            <Plus className="mr-2 h-4 w-4" />
            Add Equipment
          </Button>
        ) : undefined
      }
    >
      {/* Filters */}
      <div className="mb-4 flex items-center gap-3">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <Select value={companyFilter} onValueChange={setCompanyFilter}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Company" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Companies</SelectItem>
            {(companiesList ?? []).filter((c) => c.isActive).map((c) => (
              <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="Active">Active</SelectItem>
            <SelectItem value="Retired">Retired</SelectItem>
          </SelectContent>
        </Select>
        {data && (
          <span className="text-sm text-muted-foreground">
            {data.total} unit{data.total !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      <DataTable<EquipmentType>
        columns={columns}
        data={data?.items ?? []}
        isLoading={isLoading}
        emptyMessage="No equipment found."
        keyExtractor={(row) => row.id}
      />

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) closeDialog(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editTarget ? 'Edit Equipment' : 'Add Equipment'}
            </DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="unitNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Unit Number</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g. Unit 101"
                          {...field}
                          className={unitNumberDuplicate ? 'border-destructive focus-visible:ring-destructive' : ''}
                          onBlur={async (e) => {
                            field.onBlur();
                            const val = e.target.value.trim();
                            if (!val) { setUnitNumberDuplicate(false); return; }
                            const result = await equipmentApi.list();
                            const conflict = result.items.some(
                              (eq) => eq.unitNumber.toLowerCase() === val.toLowerCase() && eq.id !== editTarget?.id
                            );
                            setUnitNumberDuplicate(conflict);
                          }}
                        />
                      </FormControl>
                      {unitNumberDuplicate && (
                        <p className="text-[0.8rem] font-medium text-destructive">
                          Unit number already exists — please use a unique number.
                        </p>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="equipmentTypeId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Type</FormLabel>
                      <Select
                        value={field.value ? String(field.value) : ''}
                        onValueChange={(v) => field.onChange(parseInt(v, 10))}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {(equipmentTypes ?? [])
                            .filter((t) => t.isActive)
                            .map((t) => (
                              <SelectItem key={t.id} value={String(t.id)}>
                                {t.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="companyId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company</FormLabel>
                      <Select
                        value={field.value ? String(field.value) : ''}
                        onValueChange={(v) => field.onChange(parseInt(v, 10))}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select company" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {(companiesList ?? []).filter((c) => c.isActive).map((c) => (
                            <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="assetStatus"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="Active">Active</SelectItem>
                          <SelectItem value="Retired">Retired</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="locationId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Location (optional)</FormLabel>
                      <Select
                        value={field.value ? String(field.value) : 'none'}
                        onValueChange={(v) => field.onChange(v === 'none' ? null : parseInt(v, 10))}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="No location" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">— None —</SelectItem>
                          {(locationsList ?? []).filter((l) => l.isActive).map((l) => (
                            <SelectItem key={l.id} value={String(l.id)}>{l.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="vinSerial"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>VIN / Serial (optional)</FormLabel>
                      <FormControl>
                        <Input className="font-mono" placeholder="Serial or VIN" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="dateInService"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Date In Service (optional)</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. 2022 Model X" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Photo section */}
              <div className="space-y-2">
                <p className="text-sm font-medium leading-none">
                  Photo {editTarget ? '(optional — replaces existing)' : '(optional)'}
                </p>
                <div className="flex items-center gap-3">
                  {dialogPhotoSrc ? (
                    <img
                      src={dialogPhotoSrc}
                      alt="equipment photo"
                      className="w-16 h-16 rounded-md object-cover border border-border flex-shrink-0"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-md border border-dashed border-border flex items-center justify-center text-muted-foreground flex-shrink-0">
                      <Camera className="h-5 w-5" />
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) selectPhotoFile(file);
                      e.target.value = '';
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {dialogPhotoSrc ? 'Replace Photo' : 'Add Photo'}
                  </Button>
                  {pendingPhotoFile && (
                    <span className="text-xs text-muted-foreground truncate max-w-[140px]">
                      {pendingPhotoFile.name}
                    </span>
                  )}
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={closeDialog}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSaving || unitNumberDuplicate}>
                  {isSaving ? 'Saving…' : editTarget ? 'Update' : 'Add Equipment'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <PmAssignmentsDialog
        equipment={pmTarget}
        open={!!pmTarget}
        onOpenChange={(v) => { if (!v) setPmTarget(null); }}
      />

      {reportTargetId !== null && (
        <EquipmentReport
          equipmentId={reportTargetId}
          onClose={() => setReportTargetId(null)}
        />
      )}
    </PageWrapper>
  );
}
